import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui'
import DateInput from '@/components/ui/DateInput'
import { cn, quickRange, shiftRange, type DateRange } from '@/lib/utils'

const QUICK_RANGES = ['today', 'week', 'month', 'season'] as const

interface Props {
  value: DateRange
  onChange: (next: DateRange) => void
}

/**
 * The date filter shared by both reward screens: two inputs, arrows that step
 * the period by its own length, and the named shortcuts.
 *
 * The arrows only work with a complete range - with one end open there is no
 * length to step by, so they are disabled rather than guessing.
 */
export default function RangePicker({ value, onChange }: Props) {
  const { t } = useTranslation()
  const complete = Boolean(value.from && value.to)

  return (
    <div className="flex flex-wrap items-end gap-2">
      <button
        type="button"
        className="btn-secondary mb-1 px-2"
        disabled={!complete}
        title={t('rewards.range.previous')}
        aria-label={t('rewards.range.previous')}
        onClick={() => onChange(shiftRange(value, -1))}
      >
        <ChevronLeft size={16} />
      </button>

      <Field label={t('global.from')} className="w-36">
        <DateInput value={value.from} onChange={(from) => onChange({ ...value, from })} />
      </Field>
      <Field label={t('global.to')} className="w-36">
        <DateInput value={value.to} onChange={(to) => onChange({ ...value, to })} />
      </Field>

      <button
        type="button"
        className="btn-secondary mb-1 px-2"
        disabled={!complete}
        title={t('rewards.range.next')}
        aria-label={t('rewards.range.next')}
        onClick={() => onChange(shiftRange(value, 1))}
      >
        <ChevronRight size={16} />
      </button>

      {/* "Sezóna" follows the 31 July - 31 July rule the backend uses, so it
          selects exactly the same period the server would. */}
      <div className="mb-1 flex flex-wrap items-center gap-1">
        {QUICK_RANGES.map((key) => {
          const range = quickRange(key)
          const active = value.from === range.from && value.to === range.to
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(range)}
              className={cn(
                'rounded border px-2 py-1 text-[12px]',
                active
                  ? 'border-brand-500 bg-brand-50 font-medium text-brand-700'
                  : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {t(`rewards.range.${key}`)}
            </button>
          )
        })}
        {value.from || value.to ? (
          <button
            type="button"
            onClick={() => onChange({ from: '', to: '' })}
            className="rounded border border-transparent px-2 py-1 text-[12px] text-slate-500
                       hover:text-slate-800 hover:underline"
          >
            {t('rewards.range.all')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
